/**
 * Password strength scorer. Returns { score: 0-4, label, color, hint }.
 * Purely heuristic — server still enforces real policy.
 */
const COMMON_BAD = new Set([
    "admin@12345", "owner@12345", "changeme123!", "password1234",
    "p@ssword1234", "qwerty123456", "welcome12345", "letmein12345",
    "iloveyou1234", "admin12345!", "lawos@123456",
]);

export function scorePassword(pw = "") {
    if (!pw) return { score: 0, label: "Empty", color: "#404040", hint: "Type a password to start scoring." };

    if (COMMON_BAD.has(pw.toLowerCase())) {
        return { score: 0, label: "Blocked", color: "#ef4444", hint: "This is a well-known password and will be rejected." };
    }

    let s = 0;
    if (pw.length >= 12) s++;
    if (pw.length >= 16) s++;
    if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) s++;
    if (/\d/.test(pw)) s++;
    if (/[^A-Za-z0-9]/.test(pw)) s++;
    if (pw.length < 8) s = 0;

    const palette = [
        { label: "Very weak", color: "#ef4444", hint: "Use 12+ chars, mix upper/lower, digits and a symbol." },
        { label: "Weak", color: "#f97316", hint: "Add length or symbols to strengthen." },
        { label: "Okay", color: "#eab308", hint: "Decent — add a symbol or more length to make it strong." },
        { label: "Good", color: "#84cc16", hint: "Looking good. One more symbol or extra length and it's strong." },
        { label: "Strong", color: "#22c55e", hint: "💪 This password is strong enough." },
        { label: "Excellent", color: "#10b981", hint: "✨ Excellent — long, mixed, and full of entropy." },
    ];
    const idx = Math.min(s, palette.length - 1);
    return { score: idx, ...palette[idx] };
}

export function validatePassword(pw, { min_length = 12 } = {}) {
    if (!pw || pw.length < min_length) return `Password must be at least ${min_length} characters.`;
    if (!/\d/.test(pw)) return "Password must contain at least one digit.";
    if (!/[A-Za-z]/.test(pw)) return "Password must contain at least one letter.";
    if (COMMON_BAD.has(pw.toLowerCase())) return "That password is too well-known. Pick another.";
    return null;
}
